import React from 'react';
import { Box, Alert, Button, Card, CardContent, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionRouteProps {
  children: React.ReactNode;
  pageTitle?: string;
}

/**
 * SubscriptionRoute - Wraps content that requires an active subscription
 * Shows a message if the user doesn't have an active subscription
 * Allows the user to navigate to the subscription page
 */
const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({ 
  children, 
  pageTitle = 'This feature' 
}) => {
  const { hasActiveSubscription } = useAuth();
  const navigate = useNavigate();

  if (!hasActiveSubscription) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 600, boxShadow: 3 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <LockIcon sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Subscription Required
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              {pageTitle} requires an active subscription to access. Please subscribe to a plan to continue.
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Subscribe to unlock AI-powered features, API access, and more.
            </Alert>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/dashboard/subscription')}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              View Subscription Plans
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return <>{children}</>;
};

export default SubscriptionRoute;
